/**
 * @file Keyboard + gamepad input. One tiny class, no dependencies, no DOM
 * assumptions beyond "there might be a window".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE TWO RULES THAT MATTER
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. **`update()` is a FRAME FLUSH, not a poll.** It clears the edge-triggered
 *    `pressed()` set, so it must run AFTER everything that reads input in that
 *    fixed step — push it as the LAST system / last line of `level.update()`.
 *    Clearing at the top of the step would eat any key that arrived between
 *    steps (browser events land asynchronously, the sim is fixed-rate).
 * 2. **We only swallow keys the game actually uses**, and never while the user
 *    is typing. Arrows and Space scroll the page, so those get
 *    `preventDefault()`; WASD/QE do not, because stealing them from a focused
 *    text field is how you get bug reports.
 *
 * Gamepad 0's left stick is MERGED into the keyboard axis (summed, then
 * clamped/normalised) rather than overriding it — a stick nudge plus a key press
 * behaves like a stick nudge plus a key press, not like a mode switch.
 *
 * @module engine/input/Keyboard
 */

/** @typedef {{x: number, z: number}} Axis2 */

/** Physical `KeyboardEvent.code`s per movement direction. */
const AXIS_CODES = Object.freeze({
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  /** `-z` is FORWARD (away from the camera at yaw 0). */
  up: ['KeyW', 'ArrowUp'],
  down: ['KeyS', 'ArrowDown'],
});

/** Keys whose browser default we cancel (page scrolling). */
const DEFAULT_SWALLOW = Object.freeze([
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Space',
]);

/** Below this the stick is treated as centred (cheap sticks never rest at 0). */
const STICK_DEADZONE = 0.2;

export class Input {
  /**
   * @param {EventTarget|null} [target] Where to listen. Defaults to `window`
   *   when there is one (and to `null` — a valid, inert Input — in node).
   * @param {Object} [o]
   * @param {string[]} [o.swallow] `code`s to `preventDefault()`. Defaults to the
   *   arrows + Space.
   * @param {boolean} [o.gamepad=true] Merge gamepad 0's left stick.
   * @param {number} [o.deadzone=0.2]
   */
  constructor(target = typeof window !== 'undefined' ? window : null, o = {}) {
    const { swallow = DEFAULT_SWALLOW, gamepad = true, deadzone = STICK_DEADZONE } = o;

    /** @type {EventTarget|null} */
    this.target = target ?? null;
    /** @type {Set<string>} Currently held. */
    this.downKeys = new Set();
    /** @type {Set<string>} Went down since the last {@link update}. */
    this.pressedKeys = new Set();
    /** @type {Set<string>} Went up since the last {@link update}. */
    this.releasedKeys = new Set();
    /** @type {Set<string>} */
    this._swallow = new Set(swallow);
    /** @type {Map<string, Set<Function>>} */
    this._listeners = new Map();
    /** @type {boolean} */
    this.useGamepad = gamepad;
    /** @type {number} */
    this.deadzone = deadzone;
    /** @type {boolean} True while a gamepad stick is off-centre. */
    this.gamepadActive = false;
    this._disposed = false;

    /** Reused by {@link axis} so the per-frame path never allocates. */
    this._axis = { x: 0, z: 0 };

    this._onKeyDown = (e) => this._handleDown(e);
    this._onKeyUp = (e) => this._handleUp(e);
    // A lost focus (alt-tab, devtools, a modal) never delivers keyup — without
    // this the player walks into a wall forever.
    this._onBlur = () => this.clear();

    if (this.target?.addEventListener) {
      this.target.addEventListener('keydown', this._onKeyDown);
      this.target.addEventListener('keyup', this._onKeyUp);
    }
    if (typeof window !== 'undefined' && window !== this.target) {
      window.addEventListener('blur', this._onBlur);
    } else if (this.target?.addEventListener) {
      this.target.addEventListener('blur', this._onBlur);
    }
  }

  // ── queries ────────────────────────────────────────────────────────────────

  /**
   * Movement axis in CAMERA space, ready to be rotated by the camera yaw.
   * `x` = +right / -left, `z` = +towards the camera / -away from it (so W is
   * `z = -1`). Length is clamped to 1: diagonals are not faster.
   *
   * @param {Axis2} [out] Reused if given; otherwise the internal scratch object
   *   is returned (do not hold onto it across frames).
   * @returns {Axis2}
   */
  axis(out = this._axis) {
    let x = (this.anyDown(AXIS_CODES.right) ? 1 : 0) - (this.anyDown(AXIS_CODES.left) ? 1 : 0);
    let z = (this.anyDown(AXIS_CODES.down) ? 1 : 0) - (this.anyDown(AXIS_CODES.up) ? 1 : 0);

    const pad = this._stick();
    if (pad) {
      x += pad.x;
      z += pad.y;
    }

    const len = Math.hypot(x, z);
    if (len > 1) {
      x /= len;
      z /= len;
    }

    out.x = x;
    out.z = z;
    return out;
  }

  /**
   * @param {string} code A `KeyboardEvent.code`, e.g. `'KeyW'`.
   * @returns {boolean} Held right now.
   */
  down(code) {
    return this.downKeys.has(code);
  }

  /**
   * @param {string[]} codes
   * @returns {boolean} True if ANY of them is held.
   */
  anyDown(codes) {
    for (let i = 0; i < codes.length; i++) if (this.downKeys.has(codes[i])) return true;
    return false;
  }

  /**
   * Edge trigger: true only on the step where the key went down. Cleared by
   * {@link update} — see rule 1 in the file header.
   * @param {string} code
   * @returns {boolean}
   */
  pressed(code) {
    return this.pressedKeys.has(code);
  }

  /**
   * Edge trigger for key-up. Same lifetime rules as {@link pressed}.
   * @param {string} code
   * @returns {boolean}
   */
  released(code) {
    return this.releasedKeys.has(code);
  }

  // ── subscriptions ──────────────────────────────────────────────────────────

  /**
   * Fires once per physical key-down (auto-repeat is filtered out). Use `'*'` to
   * hear about every key.
   *
   * @param {string} code `KeyboardEvent.code`, or `'*'`.
   * @param {(code: string, event: any) => void} fn
   * @returns {() => void} Unsubscribe.
   */
  onKey(code, fn) {
    let set = this._listeners.get(code);
    if (!set) {
      set = new Set();
      this._listeners.set(code, set);
    }
    set.add(fn);
    return () => {
      set.delete(fn);
      if (set.size === 0) this._listeners.delete(code);
    };
  }

  // ── frame ──────────────────────────────────────────────────────────────────

  /**
   * Flushes the edge sets. Call it at the END of every fixed step, after all
   * consumers have polled.
   */
  update() {
    if (this.pressedKeys.size) this.pressedKeys.clear();
    if (this.releasedKeys.size) this.releasedKeys.clear();
  }

  /** Drops every held key (focus loss, level switch, pause menu). */
  clear() {
    this.downKeys.clear();
    this.pressedKeys.clear();
    this.releasedKeys.clear();
  }

  /** Removes every listener. Idempotent. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    if (this.target?.removeEventListener) {
      this.target.removeEventListener('keydown', this._onKeyDown);
      this.target.removeEventListener('keyup', this._onKeyUp);
      this.target.removeEventListener('blur', this._onBlur);
    }
    if (typeof window !== 'undefined') window.removeEventListener('blur', this._onBlur);
    this._listeners.clear();
    this.clear();
  }

  // ── internals ──────────────────────────────────────────────────────────────

  /** @param {any} e */
  _handleDown(e) {
    const code = e?.code;
    if (!code || isTypingTarget()) return;
    if (this._swallow.has(code) && typeof e.preventDefault === 'function') e.preventDefault();

    // `repeat` is the OS auto-repeat; the edge set must not see it.
    if (e.repeat) {
      this.downKeys.add(code);
      return;
    }

    this.downKeys.add(code);
    this.pressedKeys.add(code);
    this._emit(code, e);
  }

  /** @param {any} e */
  _handleUp(e) {
    const code = e?.code;
    if (!code) return;
    if (this._swallow.has(code) && typeof e.preventDefault === 'function' && !isTypingTarget()) {
      e.preventDefault();
    }
    // NB: no `isTypingTarget` guard on the state change itself — if focus moved
    // INTO a field while a key was held, we still want the key released.
    this.downKeys.delete(code);
    this.releasedKeys.add(code);
  }

  /**
   * @param {string} code
   * @param {any} e
   */
  _emit(code, e) {
    const exact = this._listeners.get(code);
    if (exact) for (const fn of exact) fn(code, e);
    const all = this._listeners.get('*');
    if (all) for (const fn of all) fn(code, e);
  }

  /**
   * @returns {{x: number, y: number}|null} Left stick past the deadzone, rescaled
   *   so the usable range starts at 0 (no jump when you cross the threshold).
   */
  _stick() {
    this.gamepadActive = false;
    if (!this.useGamepad) return null;
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return null;

    const pads = navigator.getGamepads();
    const pad = pads && pads[0];
    if (!pad || !pad.connected || !pad.axes || pad.axes.length < 2) return null;

    const dz = this.deadzone;
    const ax = pad.axes[0] ?? 0;
    const ay = pad.axes[1] ?? 0;
    const len = Math.hypot(ax, ay);
    if (len < dz) return null;

    const k = ((len - dz) / (1 - dz)) / len;
    this.gamepadActive = true;
    return { x: ax * k, y: ay * k };
  }
}

/**
 * @returns {boolean} True when the user is typing into something and the game
 *   must keep its hands off the keyboard.
 */
function isTypingTarget() {
  if (typeof document === 'undefined') return false;
  const el = /** @type {any} */ (document.activeElement);
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export { AXIS_CODES };
